import {
  chain,
  clamp,
  curry,
  either,
  find,
  head,
  isEmpty,
  isNil,
  map,
  path,
  pipe,
  prop,
  propEq,
  propOr,
  propSatisfies,
  zipObj,
} from 'ramda'
import { getDateDifferenceInYears } from '../dateUtils'

/**
 * takes either the policy or coverageStatus from the policy model
 * and will return the color for any status tags we want need
 * @param {string} status
 * @returns {string}
 */
export const policyAndCoverageStatusCases = status => {
  switch (status) {
    case 'ACTIVE':
    case 'ENROLLED':
    case 'CANCELLATION_PENDING':
    case 'PENDING_CANCELLATION':
      return 'good'
    case 'PENDING_APPROVAL':
    case 'PENDING_ACTIVATION':
    case 'PENDING_PAYMENT':
    case 'UPCOMING':
      return 'warn'
    case 'CANCELLED':
    case 'DELETED':
    case 'EXPIRED':
    case 'EARLY_CANCELLATION':
    case 'PENDING_ACCEPTANCE':
    case 'REPLACED':
    case 'SUSPENDED':
    case 'SUSPENDED_PENDING_REINSTATEMENT':
      return 'bad'
    default:
      return 'neutral'
  }
}

/**
 * takes either the policy or coverageStatus from the policy model
 * and will return the color for any status tags we want need
 * @param {string} status
 * @returns {string}
 */
export const getCobraCoverageStatusCases = ({ policyStatus, endDate }) => {
  switch (policyStatus) {
    case 'ACTIVE':
    case 'ENROLLED':
    case 'CANCELLATION_PENDING':
    case 'PENDING_CANCELLATION':
      return policyStatus
    case 'PENDING_APPROVAL':
    case 'PENDING_ACTIVATION':
    case 'PENDING_PAYMENT':
    case 'UPCOMING':
      return policyStatus
    case 'CANCELLED':
    case 'DELETED':
    case 'EXPIRED':
    case 'EARLY_CANCELLATION':
    case 'PENDING_ACCEPTANCE':
    case 'REPLACED':
    case 'SUSPENDED':
    case 'SUSPENDED_PENDING_REINSTATEMENT':
      return `ENDED ON ${endDate}`
    default:
      return 'neutral'
  }
}

/**
 * takes member with policy (either mini or full)
 * and will return the status determination for UI
 * Following: https://sidecarhealth.atlassian.net/wiki/spaces/TECH/pages/708673673/Account+member+policy+coverage+statuses
 * @param member
 * @returns status
 */

export const getMemberStatus = (member = {}) => {
  const { enrollmentStatus = '', policy = {} } = member

  if (enrollmentStatus === 'NOT_COVERED') {
    return 'NO_COVERAGE'
  } else if (enrollmentStatus === 'PENDING_PURCHASE') {
    return enrollmentStatus
  } else {
    return getPolicyStatus(policy)
  }
}

/**
 * takes policy (either mini or full)
 * and will return the status determination for UI
 * Following: https://sidecarhealth.atlassian.net/wiki/spaces/TECH/pages/708673673/Account+member+policy+coverage+statuses
 * @param policy
 * @returns status
 */

export const getPolicyStatus = (policy = {}) => {
  const { status = '', coverageStatus = '' } = policy

  if (status === 'SUSPENDED_PENDING_REINSTATEMENT') {
    return 'SUSPENDED'
  } else if (
    [
      'SUSPENDED',
      'CANCELLED',
      'PENDING_CANCELLATION',
      'EXPIRED',
      'UPCOMING',
    ].includes(status)
  ) {
    return status
  } else if (isEmpty(status) && isEmpty(coverageStatus)) {
    return 'NO_COVERAGE'
  } else if (
    [
      'PENDING_APPROVAL',
      'PENDING_ACCEPTANCE',
      'PENDING_ACTIVATION',
      'PENDING_PAYMENT',
    ].includes(coverageStatus)
  ) {
    return coverageStatus
  } else if (coverageStatus === 'ACTIVE' && status === 'ENROLLED') {
    return 'ACTIVE'
  } else {
    return coverageStatus
  }
}

/*
 * takes member status display text along with misstatement status
 * @param {string} memberStatus member status of the policy
 * @param {object} misstatement status of the policy
 * @return {string} status text
 */
export function getMemberStatusWithMisstatement(memberStatus, misstatement) {
  let status = memberStatus.replace(/_/g, ' ')
  if (status && status !== 'CANCELLED' && !isEmpty(misstatement)) {
    return `${status} (WITH MISSTATEMENT)`
  }
  return status
}

/**
 * takes policy (either mini or full)
 * and will return the pendingChange status determination
 * @param policy
 * @returns status
 */

export const getPendingPolicyStatus = path(['pendingChange', 'status'])

/**
 * takes in a policy startDate
 * Will return the difference between the current date and the given startDate
 * will return zero if the value is a negative integer or no startDate has been given
 * @param {date} startDate datetime string or JS date time object
 * @returns {integer} a whole number
 */

export const getCurrentPolicyYear = startDate => {
  if (!startDate) return 0

  return pipe(
    // we first get the number of years difference between
    // today and the startDate
    getDateDifferenceInYears,
    // we set the minimum to be 0
    clamp(0, Infinity),
  )(startDate, new Date())
}

const objFromListWith = curry((fn, list) => chain(zipObj, map(fn))(list))

/**
 * Extracts the benefits array into an object of k/v pairs,
 * from the benefit info array based on the current filing
 * year or whatever year gets passed in. Transforms:
 *  
 * [
    {
      benefitType: 'ALLOWANCE',
      unlimited: false,
      limitValue: 10000.0,
      used: 1500.0,
    },
    ...
  ] 
  into 
  [
    ALLOWANCE: {
      benefitType: 'ALLOWANCE',
      unlimited: false,
      limitValue: 10000.0,
      used: 1500.0,
    },
    ...
  ]
 * @param {int} year
 * @param {object} policy
 * @returns {object}
 */
export const extractBenefits = (policy = {}) => {
  if (propSatisfies(either(isEmpty, isNil), 'benefitInfo', policy)) return

  return objFromListWith(
    prop('benefitType'),
    pipe(
      propOr([], 'benefitInfo'),
      find(propEq('current', true)),
      propOr([], 'benefits'),
    )(policy),
  )
}

/**
 * Easily extract either a benefit or benefit.limitValue from a policy
 * @param {object} policy - a policy a member with benefitInfo property
 * @param {string} benefitType - the actual name of the benefit. i.e. DEDUCTIBLE
 */

export const getBenefit = (policy = {}, benefitType = '') => {
  const benefit = pipe(
    // We first get access to the benefitInfo array of the policy
    propOr([], 'benefitInfo'),
    // we make sure to get the current year entry in the benefitInfo, which is represented by "current" flag.
    find(propEq('current', true)),
    // from this current year, we extract the actual policy benefits
    propOr([], 'benefits'),
    // finally, we iterate through this benefits array to find the intended benefitType
    find(propEq('benefitType', benefitType)),
  )(policy)

  // return that benefit
  return benefit
}

/**
 * Easily extract either a benefit or benefit.limitValue from an UPCOMING policy
 * @param {object} policy - a policy a member with benefitInfo property
 * @param {string} benefitType - the actual name of the benefit. i.e. DEDUCTIBLE
 */

export const getUpcomingBenefit = (policy = {}, benefitType = '') => {
  const benefit = pipe(
    propOr([], 'policies'),
    head(),
    propOr([], 'benefitInfo'),
    head(),
    propOr([], 'benefits'),
    find(propEq('benefitType', benefitType)),
  )(policy)

  // return that benefit
  return benefit
}

/**
 * Easily determine whether a policy has a specific benefit included
 * @param {object} policy - a policy a member with benefitInfo property
 * @param {string} benefitType - the actual name of the benefit. i.e. DEDUCTIBLE
 */

export const doesBenefitExist = (policy = {}, benefitType = '') => {
  const benefit = getBenefit(policy, benefitType)

  // return that benefit
  return !isNil(benefit)
}

export const getBenefitUsagePercentage = (benefit = {}) => {
  const { limitValue = 0, used = 0 } = benefit
  if (limitValue === 0) return 0
  return used / limitValue
}

export const getBenefitAmountLeft = (benefit = {}) => {
  const { limitValue = 0, used = 0 } = benefit
  return limitValue - used
}

// returns the benefit limit value, given a policy and benefitType
export const getBenefitLimitValue = (policy, benefitType) => {
  // first it gets the benefit and then returns the limitValue
  return pipe(getBenefit, prop('limitValue'))(policy, benefitType)
}
// returns the benefit used value, given a policy and benefitType
export const getBenefitUsedValue = (policy, benefitType) => {
  // first it gets the benefit and then returns the used
  return pipe(getBenefit, prop('used'))(policy, benefitType)
}

export const extractFamilyBenefitInfo = (policy = {}) => {
  const { familyBenefitInfo = {} } = policy

  const benefits = pipe(
    propOr([], 'benefits'),
    find(propEq('benefitType', 'FAMILY_DEDUCTIBLE')),
  )(familyBenefitInfo)

  return benefits
}

export const progressBarTypes = {
  maxAllowance: 'maxAllowance',
  dashboard: 'dashboard',
  deductible: 'deductible',
  swipe: 'swipe',
}

export const getOfferingName = (offering, insuranceProduct) => {
  if (['ASO', 'GFI'].includes(insuranceProduct)) {
    return offering
  }

  switch (offering) {
    case 'ON_EXCHANGE':
      return 'On-exchange'
    case 'OFF_EXCHANGE':
      return 'Off-exchange'
    case 'FIXED_INDEMNITY':
      return 'Fixed indemnity'
    default:
      return ''
  }
}
/**
 * Checks whether the policy is a master policy
 * @param {object} policy
 * @returns {boolean}
 */
export const isHouseholdPolicy = (uuid = '') => uuid.includes('master')

export const getProductName = insuranceProduct => {
  switch (insuranceProduct) {
    case 'ACA':
      return 'ACA'
    case 'ASO':
      return 'ASO'
    case 'FIXED_INDEMNITY':
    default:
      return 'Fixed indemnity'
  }
}

export const getPolicyCost = policy => {
  const { insuranceProduct } = policy
  const premiumCost = propOr(0, 'monthlyCost', policy)
  const aptcAmount = propOr(0, 'aptcAmount', policy.parentPolicy)

  switch (insuranceProduct) {
    case 'FIXED_INDEMNITY':
      return propOr(0, 'cost', policy)
    case 'ACA':
      return premiumCost - aptcAmount
    default:
      return 0
  }
}

/**
 * We extract the members from a master policy
 * set them in an array of objects with the members first name and their benefit info.
 * @param {object} policy takes in a master policy
 * @param {string} benefitType takes in a specific benefit type (e.g. DEDUCTIBLE, FAMILY_BENEFITS, )
 * @returns {array} an array of objects with the members first name and a specified benefit
 */
export const getMembersFromMaster = (policy = {}, benefitType = '') => {
  // takes the member section of a policy
  // defaults to an empty array if no info is present
  const { policies = [] } = policy

  // returns an array build out of an object for every member of an ACA policy
  // with their first name and benefit info
  return policies.map(policy => ({
    // we look for the members first name and store that in a firstName key
    firstName: path(['member', 'firstName'], policy),
    memberUuid: prop('memberUuid', policy),
    // we search for a specific benefit type in the benefits associated with that member and store that in a benefit key in the same object.
    benefit: getBenefit(policy, benefitType),
    // getting the policy uuid for the individual, so we can look up their unique policy's expenses or service limits
    policyUuid: prop('uuid', policy),
  }))
}
